import React from 'react';

/**
 * A declarative switch statement for render flows
 *
 * The primary intention of this component is to help make finite state machine
 * driven UI simpler to develop and grok for engineers. It works as a standard
 * switch statement does _except_ there is no break statement because of this
 * execution will never fall through to the next case statement. The first case
 * statement that matches will render or the default if there are no case
 * matches. If there are no case matches and no default the component will
 * render null.
 *
 * We use a factory style creator function instead of exporting straight
 * components to support stricter type checking. You can specify the type of
 * the expression as well as the type of data that will be passed to each
 * component (as props)/render (as a function argument) prop. Passing data
 * to contents rendered as children is not supported.
 *
 * There are three ways to render the contents of the case/default match.
 * 1. component prop
 * 2. render prop
 * 3. children
 *
 * Please reference the tests of this component to see examples of each of the
 * three usage styles.
 *
 * @example
 * ```tsx
 * const Switch = createRenderSwitch<number, { multiply: boolean }>();
 *
 * function RenderNumber({ num }: { num: number }) {
 *   return (
 *      <Switch expression={num} data={{ multiply: num > 1 }}>
 *        <Switch.Case
 *          expression={2}
 *          render={({ multiply }) => <div>{multiply ? num * 10 : num}</div>}
 *        />
 *        <Switch.Default
 *          render={() => <div>{num}</div>}
 *        />
 *      </Switch>
 *   )
 * }
 */

interface DefaultCaseProps {
    component?: React.ComponentType;
    render?: () => JSX.Element | null;
    children?: React.ReactNode | React.ReactNode[];
}

interface DefaultCasePropsWithData<D> {
    component?: React.ComponentType<D>;
    render?: (props: D) => JSX.Element | null;
    children?: React.ReactNode | React.ReactNode[];
}

interface CaseProps<S> extends DefaultCaseProps {
    expression: S;
}

interface CasePropsWithData<S, D> extends DefaultCasePropsWithData<D> {
    expression: S;
}

interface SwitchProps<S> {
    expression: S;
    children: React.ReactNode | React.ReactNode[];
}

interface SwitchPropsWithData<S, D> extends SwitchProps<S> {
    data: D;
}

function render(el: React.ReactElement, data: {}) {
    const { component: Component, render, children } = el.props;

    if (Component) {
        return <Component {...data} />;
    }

    if (render) {
        return render(data);
    }

    if (children) {
        return children;
    }

    return null;
}

const DEFAULT_DATA = {};

export function createRenderSwitch<S>(): {
    (props: SwitchProps<S>): JSX.Element | null;
    Case: (props: CaseProps<S>) => null;
    Default: (props: DefaultCaseProps) => null;
};
export function createRenderSwitch<S, D extends {}>(): {
    (props: SwitchPropsWithData<S, D>): JSX.Element | null;
    Case: (props: CasePropsWithData<S, D>) => null;
    Default: (props: DefaultCasePropsWithData<D>) => null;
};
export function createRenderSwitch<S, D extends {} | undefined = undefined>() {
    function Case(_: CaseProps<S> | CasePropsWithData<S, D>) {
        return null;
    }

    function Default(_: DefaultCaseProps | DefaultCasePropsWithData<D>) {
        return null;
    }

    function Switch(props: SwitchProps<S> | SwitchPropsWithData<S, D>): JSX.Element | null {
        const { expression, children } = props;
        const optionalData = 'data' in props ? props.data : undefined;
        const data = optionalData ?? DEFAULT_DATA;

        // eslint-disable-next-line react-hooks/exhaustive-deps
        const memoizedData = React.useMemo(() => data, Object.values(data));

        const matchCase = React.Children.toArray(children).find(
            (child): child is React.ReactElement => {
                if (
                    React.isValidElement(child) &&
                    child.type === Case &&
                    child.props.expression === expression
                ) {
                    return true;
                }

                return false;
            },
        );

        if (matchCase) {
            return render(matchCase, memoizedData);
        }

        const defaultCase = React.Children.toArray(children).find(
            (child): child is React.ReactElement => {
                if (React.isValidElement(child) && child.type === Default) {
                    return true;
                }

                return false;
            },
        );

        if (defaultCase) {
            return render(defaultCase, memoizedData);
        }

        return null;
    }

    Switch.Case = Case;
    Switch.Default = Default;

    return Switch;
}
